import React, { useMemo, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import ReactSelect from "react-select";
import * as Yup from "yup";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import "react-phone-input-2/lib/style.css";
import CustomInput from "../../Common/CustomInput/CustomInput";
import ModalConfirm from "../../Common/Popup/ModalConfirm";
import Spinner from "../../Layouts/Spinner";
import PermissionsMatrix from "./PermissionsMatrix";
import "./AdminForm.css";

const STATUS_OPTIONS = [
  { value: "1", label: "Active" },
  { value: "2", label: "Inactive" },
  { value: "4", label: "Blocked" },
];

const baseSchema = {
  first_name: Yup.string()
    .matches(/^[A-Za-z ]+$/, "Only alphabets are allowed")
    .required("First name is required")
    .min(2, "Enter at least two characters"),
  last_name: Yup.string()
    .matches(/^[A-Za-z ]+$/, "Only alphabets are allowed")
    .required("Last name is required")
    .min(2, "Enter at least two characters"),
  mobile_number: Yup.string()
    .min(9, "Minimum of 9 digits")
    .max(15, "Maximum of 15 digits")
    .required("Mobile number is required"),
  email_id: Yup.string()
    .email("Enter a valid email address")
    .required("Email is required"),
};

const buildSchema = ({ mode }) =>
  Yup.object(
    mode === "create"
      ? {
          ...baseSchema,
          password: Yup.string()
            .required("Password is required")
            .min(6, "Password must be at least 6 characters"),
        }
      : {
          ...baseSchema,
          password: Yup.string().min(6, "Password must be at least 6 characters"),
        },
  );

const toInitialValues = (admin) => ({
  first_name: admin?.first_name || "",
  last_name: admin?.last_name || "",
  country_code: admin?.country_code || "+966",
  mobile_number: admin?.mobile_number || "",
  email_id: admin?.email_id || "",
  password: "",
  status: String(admin?.status ?? "1"),
  permissions: Array.isArray(admin?.permissions) ? admin.permissions : [],
  is_super_admin: Boolean(admin?.is_super_admin),
});

const AdminForm = ({
  mode,
  initialAdmin = null,
  title,
  isSelfEdit = false,
  showPermissions = true,
  onSubmit,
  submitting = false,
  backPath = "/admin",
  // Role-toggle integration (update mode):
  onRoleChange,
  roleSubmitting = false,
  activeSuperAdminCount = null,
  // Read-only mode for the audit-log "view admin" route. Disables every
  // input + button so the audit log can deep-link into a profile without
  // exposing edit affordances.
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pendingDialog, setPendingDialog] = useState(null);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: toInitialValues(initialAdmin),
    validationSchema: buildSchema({ mode }),
    onSubmit: (values) => {
      if (mode === "create" && values.is_super_admin) {
        setPendingDialog("create-super");
        return;
      }
      // Profile Save never touches the role — that endpoint is separate.
      // Catch the common mistake: user toggled the switch and clicked Save,
      // expecting the role to change too. Make it explicit.
      const initialSuper = Boolean(initialAdmin?.is_super_admin);
      if (mode === "update" && values.is_super_admin !== initialSuper) {
        setPendingDialog("save-pending-role");
        return;
      }
      onSubmit?.(values);
    },
  });

  const handlePhoneChange = (phone, country) => {
    formik.setFieldValue("country_code", `+${country.dialCode}`);
    formik.setFieldValue("mobile_number", phone.slice(country.dialCode.length));
  };

  const statusValue = STATUS_OPTIONS.find((o) => o.value === formik.values.status);

  const fieldError = (name) =>
    formik.touched[name] && formik.errors[name] ? (
      <p className="admin-form__error">{t(formik.errors[name])}</p>
    ) : null;

  const initialIsSuper = Boolean(initialAdmin?.is_super_admin);
  const currentIsSuper = formik.values.is_super_admin;
  const fullName = useMemo(() => {
    const first = (formik.values.first_name || "").trim();
    const last = (formik.values.last_name || "").trim();
    return [first, last].filter(Boolean).join(" ") || t("this admin");
  }, [formik.values.first_name, formik.values.last_name, t]);

  // In update mode, the toggle should be disabled when:
  //   - the target is the currently logged-in admin (can't flip own role)
  //   - the target IS the last active super admin (can't demote)
  const isLastActiveSuper =
    initialIsSuper && activeSuperAdminCount === 1;
  const toggleDisabled =
    readOnly ||
    submitting ||
    roleSubmitting ||
    (mode === "update" && isSelfEdit) ||
    (mode === "update" && isLastActiveSuper);

  // The Promote/Demote button only appears when the toggle has been moved
  // from its server-side state — it's a "commit" action for the in-memory flip.
  const roleChangePending =
    mode === "update" && currentIsSuper !== initialIsSuper;

  const handleSuperToggle = (e) => {
    if (toggleDisabled) return;
    formik.setFieldValue("is_super_admin", e.target.checked);
  };

  const openRoleDialog = () => {
    setPendingDialog(currentIsSuper ? "promote" : "demote");
  };

  const closeDialog = () => {
    if (roleSubmitting) return;
    setPendingDialog(null);
  };

  const confirmCreateSuper = () => {
    setPendingDialog(null);
    onSubmit?.(formik.values);
  };

  const confirmRoleChange = async () => {
    if (!onRoleChange || !initialAdmin?.id) return;
    await onRoleChange(initialAdmin.id, currentIsSuper);
    setPendingDialog(null);
  };

  // User clicked Save while the toggle differs from the server-side role.
  // Save only the profile/permissions; the role stays as it is on the server.
  const confirmSaveWithoutRoleChange = () => {
    setPendingDialog(null);
    formik.setFieldValue("is_super_admin", initialIsSuper);
    onSubmit?.({ ...formik.values, is_super_admin: initialIsSuper });
  };

  const dialogProps = (() => {
    switch (pendingDialog) {
      case "create-super":
        return {
          title: t("Create as Super Admin?"),
          message: (
            <>
              {t("Create")} <strong>{fullName}</strong>{" "}
              {t(
                "as a Super Admin? They'll have full access and can manage other admins from their first login.",
              )}
            </>
          ),
          confirmLabel: t("Create as Super Admin"),
          confirmColor: "primary",
          loading: submitting,
          onConfirm: confirmCreateSuper,
        };
      case "promote":
        return {
          title: t("Promote to Super Admin?"),
          message: (
            <>
              {t("Make")} <strong>{fullName}</strong>{" "}
              {t(
                "a Super Admin? They'll get full access and be able to manage other admins. They'll be signed out and need to log in again.",
              )}
            </>
          ),
          confirmLabel: t("Promote"),
          confirmColor: "primary",
          loading: roleSubmitting,
          onConfirm: confirmRoleChange,
        };
      case "demote":
        return {
          title: t("Remove Super Admin?"),
          message: (
            <>
              {t("Remove Super Admin from")} <strong>{fullName}</strong>?{" "}
              {t(
                "They'll only have access to what's checked in the permissions below. They'll be signed out and need to log in again.",
              )}
            </>
          ),
          confirmLabel: t("Demote"),
          confirmColor: "danger",
          loading: roleSubmitting,
          onConfirm: confirmRoleChange,
        };
      case "save-pending-role": {
        const buttonLabel = currentIsSuper
          ? t("Promote to Super Admin")
          : t("Remove Super Admin");
        return {
          title: t("Role change isn't saved here"),
          message: (
            <>
              {t(
                "You moved the Super Admin switch but didn't apply it. Save will only update the profile and permissions — the role stays as it is. To change the role, click",
              )}{" "}
              <strong>“{buttonLabel}”</strong> {t("first.")}
            </>
          ),
          confirmLabel: t("Save without changing role"),
          cancelLabel: t("Go back"),
          confirmColor: "primary",
          loading: submitting,
          onConfirm: confirmSaveWithoutRoleChange,
        };
      }
      default:
        return null;
    }
  })();

  return (
    <div className="admin-form">
      <div className="admin-form__header">
        <button
          type="button"
          className="admin-form__back"
          onClick={() => navigate(backPath)}
          aria-label="Back"
        >
          <ArrowLeft size={16} /> {t("Back")}
        </button>
        <div className="admin-form__title-wrap">
          <h2 className="admin-form__title">{title}</h2>
          {initialIsSuper && (
            <span className="admin-pill admin-pill--primary">
              <ShieldCheck size={12} /> {t("Super Admin")}
            </span>
          )}
        </div>
      </div>

      <form className="admin-form__body" onSubmit={formik.handleSubmit} noValidate>
        <section className="admin-form__section">
          <div className="admin-form__section-header">
            <h3>{t("Profile details")}</h3>
            <p>{t("Personal information and login credentials.")}</p>
          </div>
          <div className="admin-form__grid">
            <div className="admin-form__field">
              <label htmlFor="first_name">{t("First Name")}</label>
              <CustomInput
                id="first_name"
                name="first_name"
                value={formik.values.first_name}
                placeholder={t("First Name")}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                touched={formik.touched}
                errors={formik.errors}
                disabled={readOnly}
              />
              {fieldError("first_name")}
            </div>

            <div className="admin-form__field">
              <label htmlFor="last_name">{t("Last Name")}</label>
              <CustomInput
                id="last_name"
                name="last_name"
                value={formik.values.last_name}
                placeholder={t("Last Name")}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                touched={formik.touched}
                errors={formik.errors}
                disabled={readOnly}
              />
              {fieldError("last_name")}
            </div>

            <div className="admin-form__field">
              <label htmlFor="email_id">{t("Email")}</label>
              <CustomInput
                id="email_id"
                name="email_id"
                value={formik.values.email_id}
                placeholder="name@example.com"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                touched={formik.touched}
                errors={formik.errors}
                disabled={readOnly}
              />
              {fieldError("email_id")}
            </div>

            <div className="admin-form__field">
              <label>{t("Mobile Number")}</label>
              <PhoneInput
                country="sa"
                value={`${formik.values.country_code || ""}${formik.values.mobile_number || ""}`.replace("+", "")}
                onChange={handlePhoneChange}
                onBlur={() => formik.setFieldTouched("mobile_number", true)}
                inputClass="admin-form__phone"
                disabled={readOnly}
              />
              {fieldError("mobile_number")}
            </div>

            {!readOnly && (
              <div className="admin-form__field">
                <label htmlFor="password">
                  {mode === "create" ? t("Password") : t("New Password (optional)")}
                </label>
                <CustomInput
                  id="password"
                  name="password"
                  type="password"
                  value={formik.values.password}
                  placeholder={mode === "create" ? t("Set a password") : t("Leave blank to keep current")}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  touched={formik.touched}
                  errors={formik.errors}
                />
                {fieldError("password")}
              </div>
            )}

            {!isSelfEdit && (
              <div className="admin-form__field">
                <label>{t("Status")}</label>
                <ReactSelect
                  classNamePrefix="admin-form-select"
                  value={statusValue}
                  options={STATUS_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                  onChange={(opt) => formik.setFieldValue("status", opt.value)}
                  onBlur={() => formik.setFieldTouched("status", true)}
                  isDisabled={readOnly}
                />
                {fieldError("status")}
              </div>
            )}
          </div>
        </section>

        {showPermissions && (
          <section className="admin-form__section">
            <div className="admin-form__role">
              <div className="admin-form__role-info">
                <h3>{t("Super Admin")}</h3>
                <p>
                  {t(
                    "Super admins bypass per-module permissions and can manage other admins.",
                  )}
                </p>
              </div>
              <div className="admin-form__role-actions">
                <label
                  className={`admin-form__switch${
                    toggleDisabled ? " admin-form__switch--disabled" : ""
                  }`}
                  title={
                    isSelfEdit
                      ? t("You can't change your own super admin status.")
                      : isLastActiveSuper
                        ? t("Can't remove the last active super admin.")
                        : undefined
                  }
                >
                  <input
                    type="checkbox"
                    checked={currentIsSuper}
                    disabled={toggleDisabled}
                    onChange={handleSuperToggle}
                    aria-label={t("Super Admin")}
                  />
                  <span className="admin-form__switch-track" aria-hidden />
                </label>
                {!readOnly && roleChangePending && (
                  <div className="admin-form__role-pending">
                    <span className="admin-form__role-pending-dot" aria-hidden />
                    <span>{t("Not applied yet")}</span>
                    <button
                      type="button"
                      className={`admin-form__role-btn admin-form__role-btn--${
                        currentIsSuper ? "promote" : "demote"
                      }`}
                      onClick={openRoleDialog}
                      disabled={roleSubmitting}
                    >
                      {roleSubmitting ? (
                        <Spinner />
                      ) : currentIsSuper ? (
                        t("Promote to Super Admin")
                      ) : (
                        t("Remove Super Admin")
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {currentIsSuper && (
              <div className="admin-form__role-banner">
                <ShieldCheck size={14} />
                <span>
                  {t(
                    "Super admins have full access to everything. Turn off Super Admin to set specific permissions.",
                  )}
                </span>
              </div>
            )}

            <PermissionsMatrix
              value={formik.values.permissions}
              onChange={(next) => formik.setFieldValue("permissions", next)}
              forceAllChecked={currentIsSuper}
              disabled={readOnly}
            />
          </section>
        )}

        {!readOnly && (
          <footer className="admin-form__footer">
            <button
              type="button"
              className="admin-form__cancel"
              onClick={() => navigate(backPath)}
              disabled={submitting}
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              className="admin-form__submit"
              disabled={submitting}
            >
              {submitting ? (
                <Spinner />
              ) : mode === "create" ? (
                t("Create admin")
              ) : (
                t("Save changes")
              )}
            </button>
          </footer>
        )}
      </form>

      {dialogProps && (
        <ModalConfirm
          open
          onCancel={closeDialog}
          {...dialogProps}
        />
      )}
    </div>
  );
};

export default AdminForm;
